import React, { useEffect, useState } from 'react';
import { ToolTip } from '@repo/common';
import { useDashboardBreakpoint } from '@repo/common';
import { ReactComponent as CardBg } from '@repo/common/assets/images/dashboardCardBg.svg';
import { getUpcomingConfirmedCount } from '../../../../api/booking/booking.api';

const READINESS_SCORE_STORAGE_KEY = 'pathfinderReadinessScore';
const READINESS_STAGE_STORAGE_KEY = 'pathfinderReadinessStage';
const DEFAULT_READINESS_SCORE = 65;

const STAGE_MESSAGE = {
  PRE_ENTRY: "Just getting started — let's build your base",
  ENTRY: "You're on your way — keep building",
  GROWTH: 'Good progress — a few steps to go',
  INTEGRATION: "Almost there — you're nearly ready",
  SETTLEMENT: "You're ready — keep it up!",
};
const DEFAULT_STAGE_MESSAGE = 'Complete actions to raise your score';

const getStoredReadinessStage = () =>
  localStorage.getItem(READINESS_STAGE_STORAGE_KEY) || null;

const getStoredReadinessScore = () => {
  const storedScore = Number(localStorage.getItem(READINESS_SCORE_STORAGE_KEY));

  if (!Number.isFinite(storedScore)) return DEFAULT_READINESS_SCORE;

  return Math.max(0, Math.min(100, storedScore));
};

// 소수점 1자리까지 유지
const normalizeScore = (score) => {
  const numericScore = Number(score);
  if (!Number.isFinite(numericScore)) return null;
  const score100 = numericScore <= 5 ? numericScore * 20 : numericScore;
  return Math.round(Math.max(0, Math.min(100, score100)) * 10) / 10;
};

const ReadinessScore = ({
  forceShow = false,
  score: scoreProp,
  stage: stageProp,
}) => {
  const [upcomingCount, setUpcomingCount] = useState(0);
  const [storedScore] = useState(getStoredReadinessScore);
  const [storedStage] = useState(getStoredReadinessStage);
  const bp = useDashboardBreakpoint();
  const isMobile = ['mobileMini', 'mobile', 'tabletH'].includes(bp);
  const score = normalizeScore(scoreProp) ?? storedScore;
  const percent = score;
  const stage = stageProp ?? storedStage;
  const stageMessage = STAGE_MESSAGE[stage] ?? DEFAULT_STAGE_MESSAGE;

  useEffect(() => {
    const fetchUpcomingCount = async () => {
      try {
        const count = await getUpcomingConfirmedCount();
        setUpcomingCount(count);
      } catch (error) {
        console.error(error);
      }
    };

    fetchUpcomingCount();
  }, []);

  if (isMobile && !forceShow) return null;

  return (
    <div
      className={`relative ${forceShow ? 'w-full max-w-[480px] h-[200px]' : 'w-[320px] h-[200px]'}`}
    >
      <div
        className={`absolute top-0 right-0 flex gap-[15px] ${
          forceShow ? 'pr-2' : 'pr-5'
        } bg-transparent`}
      >
        <div className='flex flex-col items-center text-green-deep02'>
          <span
            className={`${forceShow ? 'typo-b18Strong' : 'typo-b18Strong'}`}
          >
            {upcomingCount}
          </span>
          <span className={`${forceShow ? 'typo-b11' : 'typo-b11'}`}>
            Workshops
          </span>
        </div>
        <div className='flex flex-col items-center text-green-deep02'>
          <span
            className={`${forceShow ? 'typo-b18Strong' : 'typo-b18Strong'}`}
          >
            0
          </span>
          <span className={`${forceShow ? 'typo-b11' : 'typo-b11'}`}>
            Courses
          </span>
        </div>
      </div>

      <CardBg
        preserveAspectRatio='none'
        className='absolute inset-0 w-full h-full'
      />

      <div
        className={`relative z-10 flex flex-col w-full h-full ${
          forceShow ? 'px-5 pt-6 pb-5' : 'px-5 pt-4 pb-5'
        } justify-between`}
      >
        <div className='flex flex-col items-start gap-0.5 text-green-deep04 pb-3'>
          <div className={`${forceShow ? 'typo-b14Strong' : 'typo-b14Strong'}`}>
            Readiness Score
          </div>
          <div className='typo-b12'>Your overall readiness</div>
        </div>

        <div
          className='relative flex flex-col gap-[6px] pb-4 h-[78px]'
          style={{ zIndex: 20 }}
        >
          <div className='h-[44px]' />
          <div
            className='absolute top-0 z-50'
            style={{
              left: `${percent}%`,
              transform: 'translateX(-50%)',
            }}
          >
            <ToolTip
              label={score}
              position='bottom'
              align='center'
              containerClass='text-green-deep04 rounded-[15px] px-[11px] py-2 shadow-2'
            />
          </div>

          <div className='relative w-full h-3 rounded-full bg-green-light02'>
            <div
              className='absolute top-0 left-0 h-full rounded-full bg-green-deep01'
              style={{ width: `${percent}%` }}
            />
          </div>
        </div>

        <div className='typo-b14Strong text-green-deep01'>
          {stageMessage}
        </div>
      </div>
    </div>
  );
};

export default ReadinessScore;
